// @generated by TypeGen from LogbookApi/NoteApi.swift - DO NOT EDIT

import type { INoteResponse, INoteType } from "../LogbookModel/Note"
import type { INoteAttachmentResponse } from "../LogbookModel/NoteAttachment"
import { IShipUserLevel } from "../LogbookModel/ShipUser"
import { assertTarget } from "./ApiRuntime"
import type { ApiArgs, ApiClient, ApiTarget, ApiUser } from "./ApiRuntime"

export interface INoteCreateRequest {
  date?: string;
  note?: string;
  noteExtra?: string;
  noteType?: INoteType;
  sortOrder?: number;
  title?: string;
}

export interface INoteUpdateRequest {
  date?: string;
  note?: string;
  noteExtra?: string;
  sortOrder?: number;
  title?: string;
}

export interface NoteApiEndpoints {
  "DELETE /api/ships/:shipId/notes/:noteId/attachments/:attachmentId": {
    params: { shipId: number; noteId: number; attachmentId: number };
    query: {};
    request: never;
    response: INoteAttachmentResponse;
    targets: ApiTarget[];
    permission: { ship: IShipUserLevel };
  };
  "GET /ships/:shipId/notes/:noteId/attachments/:attachmentId/download": {
    params: { shipId: number; noteId: number; attachmentId: number };
    query: {};
    request: never;
    response: Blob;
    targets: ApiTarget[];
    permission: { ship: IShipUserLevel };
  };
  "POST /api/ships/:shipId/cruises/:cruiseId/notes": {
    params: { shipId: number; cruiseId: number };
    query: {};
    request: INoteCreateRequest;
    response: INoteResponse;
    targets: ApiTarget[];
    permission: { ship: IShipUserLevel };
  };
  "POST /api/ships/:shipId/days/:dayId/notes": {
    params: { shipId: number; dayId: number };
    query: {};
    request: INoteCreateRequest;
    response: INoteResponse;
    targets: ApiTarget[];
    permission: { ship: IShipUserLevel };
  };
  "POST /api/ships/:shipId/notes/:noteId/attachment": {
    params: { shipId: number; noteId: number };
    query: {};
    request: { attachment: File; date?: string; description?: string };
    response: INoteAttachmentResponse;
    targets: ApiTarget[];
    permission: { ship: IShipUserLevel };
  };
  "POST /api/ships/:shipId/trips/:tripId/notes": {
    params: { shipId: number; tripId: number };
    query: {};
    request: INoteCreateRequest;
    response: INoteResponse;
    targets: ApiTarget[];
    permission: { ship: IShipUserLevel };
  };
  "PUT /api/ships/:shipId/notes/:noteId": {
    params: { shipId: number; noteId: number };
    query: {};
    request: INoteUpdateRequest;
    response: INoteResponse;
    targets: ApiTarget[];
    permission: { ship: IShipUserLevel };
  };
}

export const noteApiEndpoints = {
  "DELETE /api/ships/:shipId/notes/:noteId/attachments/:attachmentId": { targets: ["app"], permission: { ship: IShipUserLevel.editCurrent } },
  "GET /ships/:shipId/notes/:noteId/attachments/:attachmentId/download": { targets: ["app"], permission: { ship: IShipUserLevel.viewCurrent } },
  "POST /api/ships/:shipId/cruises/:cruiseId/notes": { targets: ["app", "server"], permission: { ship: IShipUserLevel.editCurrent } },
  "POST /api/ships/:shipId/days/:dayId/notes": { targets: ["app", "server"], permission: { ship: IShipUserLevel.editCurrent } },
  "POST /api/ships/:shipId/notes/:noteId/attachment": { targets: ["app"], permission: { ship: IShipUserLevel.primary } },
  "POST /api/ships/:shipId/trips/:tripId/notes": { targets: ["app", "server"], permission: { ship: IShipUserLevel.editCurrent } },
  "PUT /api/ships/:shipId/notes/:noteId": { targets: ["app", "server"], permission: { ship: IShipUserLevel.editCurrent } },
} as const;

export class NoteApi {
  constructor(private client: ApiClient) {}

  noteAttachments = {
    delete: (a: ApiArgs<NoteApiEndpoints, "DELETE /api/ships/:shipId/notes/:noteId/attachments/:attachmentId">) => ({
      call: (): Promise<INoteAttachmentResponse> => {
        assertTarget(this.client, "DELETE /api/ships/:shipId/notes/:noteId/attachments/:attachmentId", noteApiEndpoints["DELETE /api/ships/:shipId/notes/:noteId/attachments/:attachmentId"].targets);
        return this.client.call<INoteAttachmentResponse>("DELETE /api/ships/:shipId/notes/:noteId/attachments/:attachmentId", a);
      },
      url: (): string => this.client.url("DELETE /api/ships/:shipId/notes/:noteId/attachments/:attachmentId", a),
      available: (user?: ApiUser): boolean => user != null && (user.shipLevel(a.params.shipId) ?? -1) >= IShipUserLevel.editCurrent,
    }),
    download: (a: ApiArgs<NoteApiEndpoints, "GET /ships/:shipId/notes/:noteId/attachments/:attachmentId/download">) => ({
      call: (): Promise<Blob> => {
        assertTarget(this.client, "GET /ships/:shipId/notes/:noteId/attachments/:attachmentId/download", noteApiEndpoints["GET /ships/:shipId/notes/:noteId/attachments/:attachmentId/download"].targets);
        return this.client.call<Blob>("GET /ships/:shipId/notes/:noteId/attachments/:attachmentId/download", a);
      },
      url: (): string => this.client.url("GET /ships/:shipId/notes/:noteId/attachments/:attachmentId/download", a),
      available: (user?: ApiUser): boolean => user != null && (user.shipLevel(a.params.shipId) ?? -1) >= IShipUserLevel.viewCurrent,
    }),
    create: (a: ApiArgs<NoteApiEndpoints, "POST /api/ships/:shipId/notes/:noteId/attachment">) => ({
      call: (): Promise<INoteAttachmentResponse> => {
        assertTarget(this.client, "POST /api/ships/:shipId/notes/:noteId/attachment", noteApiEndpoints["POST /api/ships/:shipId/notes/:noteId/attachment"].targets);
        return this.client.call<INoteAttachmentResponse>("POST /api/ships/:shipId/notes/:noteId/attachment", a);
      },
      url: (): string => this.client.url("POST /api/ships/:shipId/notes/:noteId/attachment", a),
      available: (user?: ApiUser): boolean => user != null && (user.shipLevel(a.params.shipId) ?? -1) >= IShipUserLevel.primary,
    }),
  };

  notes = {
    createForCruise: (a: ApiArgs<NoteApiEndpoints, "POST /api/ships/:shipId/cruises/:cruiseId/notes">) => ({
      call: (): Promise<INoteResponse> => {
        assertTarget(this.client, "POST /api/ships/:shipId/cruises/:cruiseId/notes", noteApiEndpoints["POST /api/ships/:shipId/cruises/:cruiseId/notes"].targets);
        return this.client.call<INoteResponse>("POST /api/ships/:shipId/cruises/:cruiseId/notes", a);
      },
      url: (): string => this.client.url("POST /api/ships/:shipId/cruises/:cruiseId/notes", a),
      available: (user?: ApiUser): boolean => user != null && (user.shipLevel(a.params.shipId) ?? -1) >= IShipUserLevel.editCurrent,
    }),
    createForDay: (a: ApiArgs<NoteApiEndpoints, "POST /api/ships/:shipId/days/:dayId/notes">) => ({
      call: (): Promise<INoteResponse> => {
        assertTarget(this.client, "POST /api/ships/:shipId/days/:dayId/notes", noteApiEndpoints["POST /api/ships/:shipId/days/:dayId/notes"].targets);
        return this.client.call<INoteResponse>("POST /api/ships/:shipId/days/:dayId/notes", a);
      },
      url: (): string => this.client.url("POST /api/ships/:shipId/days/:dayId/notes", a),
      available: (user?: ApiUser): boolean => user != null && (user.shipLevel(a.params.shipId) ?? -1) >= IShipUserLevel.editCurrent,
    }),
    createForTrip: (a: ApiArgs<NoteApiEndpoints, "POST /api/ships/:shipId/trips/:tripId/notes">) => ({
      call: (): Promise<INoteResponse> => {
        assertTarget(this.client, "POST /api/ships/:shipId/trips/:tripId/notes", noteApiEndpoints["POST /api/ships/:shipId/trips/:tripId/notes"].targets);
        return this.client.call<INoteResponse>("POST /api/ships/:shipId/trips/:tripId/notes", a);
      },
      url: (): string => this.client.url("POST /api/ships/:shipId/trips/:tripId/notes", a),
      available: (user?: ApiUser): boolean => user != null && (user.shipLevel(a.params.shipId) ?? -1) >= IShipUserLevel.editCurrent,
    }),
    update: (a: ApiArgs<NoteApiEndpoints, "PUT /api/ships/:shipId/notes/:noteId">) => ({
      call: (): Promise<INoteResponse> => {
        assertTarget(this.client, "PUT /api/ships/:shipId/notes/:noteId", noteApiEndpoints["PUT /api/ships/:shipId/notes/:noteId"].targets);
        return this.client.call<INoteResponse>("PUT /api/ships/:shipId/notes/:noteId", a);
      },
      url: (): string => this.client.url("PUT /api/ships/:shipId/notes/:noteId", a),
      available: (user?: ApiUser): boolean => user != null && (user.shipLevel(a.params.shipId) ?? -1) >= IShipUserLevel.editCurrent,
    }),
  };
}
